import { Box, Grid, IconButton, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import {
  getGetGoalModelingAssetOptionsQueryKey,
  getGetGoalModelingDetailQueryKey,
  getGetModelingAvailableAmountQueryKey,
  getGetModelingAvailableAssetAmountQueryKey,
  getGetModelingRecapitulationQueryKey,
  useCreateGoalModelingAllocatedAmount,
  useDeleteGoalModelingAllocatedAmount,
  useGetModelingAvailableAmount,
  useGetModelingAvailableAssetAmount,
  useUpdateGoalModelingAllocatedAmount,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type AllocatedAmount,
  type Amount,
  AmountFrequency,
  type CoverageSection,
  CreateAllocatedAmountBodySourceType,
  type GoalOverview,
  type TimeDuration,
} from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { FC } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AmountInputCard } from "./AmountInputCard";
import { CoverageSelect } from "./CoverageSelect";
import { GoalSummaryCard } from "./GoalSummaryCard";
import { RecurringAmountInput } from "./RecurringAmountInput";

const ZERO_AMOUNT: Amount = { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly };
const ZERO_COVERAGE_SECTION: CoverageSection = {
  requiredAmount: 0,
  coveredAmount: 0,
  coveredPercent: 0,
  requiredOneTimeAmount: 0,
  coveredOneTimeAmount: 0,
  missingAmount: 0,
};

type Props = {
  readonly goalId: string;
  readonly goalOverview: GoalOverview;
  readonly name: string;
  readonly interestRate: number;
  readonly inflationValue: number;
  readonly feeValue: number;
  readonly goalDuration: TimeDuration | null;
  readonly targetAmount: Amount | null;
  readonly allocatedAmount?: AllocatedAmount | null;
  readonly allocatedAssetAmounts: AllocatedAmount[];
  readonly coverageSection: CoverageSection | null;
  readonly readOnly: boolean;
};

const CellWithBackground = styled(Grid)`
  background: #f2f2f2;
  padding: ${(props) => props.theme.spacing(0, Spacing.L)};
`;

export const GoalModelingRow: FC<Props> = ({
  goalId,
  allocatedAmount,
  allocatedAssetAmounts,
  feeValue,
  goalDuration,
  goalOverview,
  interestRate,
  name,
  targetAmount,
  coverageSection: coverageSectionProp,
  inflationValue,
  readOnly = false,
}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const coverageSection = coverageSectionProp ?? ZERO_COVERAGE_SECTION;

  const { data: availableAmount } = useGetModelingAvailableAmount(analysisId, versionId);
  const { data: availableAssetAmount } = useGetModelingAvailableAssetAmount(analysisId, versionId);

  const recurringAmount = Number(allocatedAmount?.amount?.value ?? 0).toString();

  const invalidateGoalModelingDetail = () =>
    queryClient.invalidateQueries({ queryKey: getGetGoalModelingDetailQueryKey(analysisId, versionId, goalId) });

  const { mutate: createAllocatedAmount } = useCreateGoalModelingAllocatedAmount();
  const { mutate: updateAllocatedAmount } = useUpdateGoalModelingAllocatedAmount();
  const { mutate: deleteAllocatedAmount } = useDeleteGoalModelingAllocatedAmount();

  const createOrUpdateRecurringAmount = (value: number) => {
    if (allocatedAmount?.allocatedAmountId != null) {
      updateAllocatedAmount(
        {
          analysisId,
          versionId,
          goalId,
          allocatedAmountId: allocatedAmount.allocatedAmountId,
          data: { amount: { ...(allocatedAmount.amount ?? ZERO_AMOUNT), value } },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
            invalidateGoalModelingDetail();
            queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
            toast.success(t(Resources.AnalysisDetail.Modeling.ResourcesToInvestEdited));
          },
          onError: () => toast.error(t(Resources.Common.Error)),
        },
      );
    } else {
      createAllocatedAmount(
        {
          analysisId,
          versionId,
          goalId,
          data: {
            amount: { value, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            sourceType: CreateAllocatedAmountBodySourceType.available_amount,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetModelingAvailableAmountQueryKey(analysisId, versionId) });
            invalidateGoalModelingDetail();
            toast.success(t(Resources.AnalysisDetail.Modeling.ResourcesToInvestEdited));
          },
          onError: () => toast.error(t(Resources.Common.Error)),
        },
      );
    }
  };

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={4}>
        <GoalSummaryCard
          name={name}
          feeValue={feeValue}
          goalId={goalId}
          goalDuration={goalDuration}
          rawGoalOverview={goalOverview}
          interestRate={interestRate}
          coverageSection={coverageSection}
          targetAmount={targetAmount ?? ZERO_AMOUNT}
          inflationValue={inflationValue}
        />
      </Grid>
      <CellWithBackground size={4} flex={1}>
        <Stack spacing={Spacing.M}>
          {allocatedAssetAmounts.map((assetAmount, index) => {
            return (
              <AmountInputCard key={assetAmount.allocatedAmountId ?? `${assetAmount.assetItemGuid}-${index}`}>
                <Stack spacing={Spacing.S}>
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {assetAmount.labels?.name}
                      </Typography>
                      <Typography variant="caption" color="secondary">
                        {!!assetAmount.labels?.clientLabel && `${assetAmount.labels.clientLabel} • `}
                        {!!assetAmount.labels?.institutionLabel && `${assetAmount.labels.institutionLabel}`}
                      </Typography>
                    </Box>
                    <Box display="flex" width={180}>
                      <TextInput
                        disabled={readOnly}
                        error={availableAssetAmount && availableAssetAmount.amount.value < 0}
                        value={Math.round(Number(assetAmount.amount?.value ?? 0)).toString()}
                        mask={[
                          {
                            mask: Number,
                            scale: 0,
                            min: 0,
                            thousandsSeparator: " ",
                          },
                        ]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.CZKSuffix)}
                          </Typography>
                        }
                        onBlur={(event) => {
                          if (assetAmount.allocatedAmountId == null) return;

                          updateAllocatedAmount(
                            {
                              analysisId,
                              versionId,
                              goalId,
                              allocatedAmountId: assetAmount.allocatedAmountId,
                              data: {
                                amount: {
                                  ...(assetAmount.amount ?? ZERO_AMOUNT),
                                  value: Number(event.target.value.replaceAll(" ", "")),
                                },
                              },
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries({
                                  queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId),
                                });
                                invalidateGoalModelingDetail();
                                queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
                                toast.success(t(Resources.AnalysisDetail.Modeling.AssetsEdited));
                              },
                              onError: () => toast.error(t(Resources.Common.Error)),
                            },
                          );
                        }}
                      />
                      <Box marginLeft={Spacing.M}>
                        <IconButton
                          disabled={readOnly}
                          onClick={() => {
                            if (readOnly || assetAmount.allocatedAmountId == null) return;

                            deleteAllocatedAmount(
                              { analysisId, versionId, goalId, allocatedAmountId: assetAmount.allocatedAmountId },
                              {
                                onSuccess: () => {
                                  queryClient.invalidateQueries({
                                    queryKey: getGetModelingAvailableAssetAmountQueryKey(analysisId, versionId),
                                  });
                                  invalidateGoalModelingDetail();
                                  queryClient.invalidateQueries({
                                    queryKey: getGetGoalModelingAssetOptionsQueryKey(analysisId, versionId, goalId),
                                  });
                                  queryClient.invalidateQueries({ queryKey: getGetModelingRecapitulationQueryKey(analysisId, versionId) });
                                  toast.success(t(Resources.AnalysisDetail.Modeling.AssetsDeleted));
                                },
                                onError: () => toast.error(t(Resources.Common.Error)),
                              },
                            );
                          }}
                        >
                          <BinIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </AmountInputCard>
            );
          })}
          {!readOnly && <CoverageSelect goalId={goalId} />}
        </Stack>
      </CellWithBackground>
      <CellWithBackground size={4} flex={1}>
        <AmountInputCard>
          <RecurringAmountInput
            readOnly={readOnly}
            error={availableAmount && availableAmount.amount.value < 0}
            value={Number(recurringAmount)}
            isDecrementDisabled={Number(recurringAmount) <= 0 || readOnly}
            isIncrementDisabled={Number(recurringAmount) >= Math.round(coverageSection.requiredAmount ?? 0) || readOnly}
            onBlur={(event) => {
              createOrUpdateRecurringAmount(Number(event.target.value.replaceAll(" ", "")));
            }}
            onIncrement={() => {
              const requiredAmount = Math.round(coverageSection.requiredAmount ?? 0);
              const nextValue = requiredAmount - Number(recurringAmount) > 1000 ? Number(recurringAmount) + 1000 : requiredAmount;
              createOrUpdateRecurringAmount(nextValue);
            }}
            onDecrement={() => {
              if (!allocatedAmount) return;
              const nextValue = Number(recurringAmount) - 1000 < 0 ? 0 : Number(recurringAmount) - 1000;
              createOrUpdateRecurringAmount(nextValue);
            }}
          />
        </AmountInputCard>
      </CellWithBackground>
    </Grid>
  );
};
